import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/EditOutlined';
import { useGraphEditorStore } from '@/graph-store';
import { MaterialInfo } from './MaterialInfo';
import { OperationInfo } from './OperationInfo';
import { EdgeInfo } from './EdgeInfo';
import { GroupInfo } from './GroupInfo';
import { LineageInfo } from './LineageInfo';

// ─── SelectedCell union per SDD-06 §8.2 ────────────────────────────────────

export type SelectedCell =
  | { cellType: 'material'; id: string }
  | { cellType: 'operation'; operationNodeId: string; label: string }
  | { cellType: 'lineage'; sourceId: string; targetId: string }
  | { cellType: 'group'; id: string }
  | { cellType: 'edge'; id: string }
  | null;

interface InfoTabProps {
  selectedCell: SelectedCell;
  onEditRequested: (nodeId: string) => void;
}

export const InfoTab: React.FC<InfoTabProps> = ({ selectedCell, onEditRequested }) => {
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);
  const flowEdges = useGraphEditorStore((s) => s.graphData.flowEdges);
  const groups = useGraphEditorStore((s) => s.graphData.groups);

  if (!selectedCell) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          Click a node or edge on the canvas to see details.
        </Typography>
      </Box>
    );
  }

  // ── Material ────────────────────────────────────────────────────────────
  if (selectedCell.cellType === 'material') {
    const material = materialNodes[selectedCell.id];
    if (!material) return <EmptyState />;

    return (
      <InfoWrapper
        title={`Material: ${material.name}`}
      >
        <MaterialInfo data={{ id: selectedCell.id, ...material }} />
      </InfoWrapper>
    );
  }

  // ── Operation (from edge click or complex-op node click) ────────────────
  if (selectedCell.cellType === 'operation') {
    const operation = operationNodes[selectedCell.operationNodeId];
    if (!operation) return <EmptyState />;

    const inputMaterials = Object.values(flowEdges)
      .filter((e) => e.targetId === selectedCell.operationNodeId && materialNodes[e.sourceId])
      .map((e) => ({ id: e.sourceId, ...materialNodes[e.sourceId] }));

    const outputMaterials = Object.values(flowEdges)
      .filter((e) => e.sourceId === selectedCell.operationNodeId && materialNodes[e.targetId])
      .map((e) => ({ id: e.targetId, ...materialNodes[e.targetId] }));

    return (
      <InfoWrapper
        title={`Operation: ${operation.displayName}`}
        showEdit
        onEdit={() => onEditRequested(selectedCell.operationNodeId)}
      >
        <OperationInfo
          data={{ id: selectedCell.operationNodeId, ...operation }}
          inputMaterials={inputMaterials}
          outputMaterials={outputMaterials}
        />
      </InfoWrapper>
    );
  }

  // ── Lineage edge ────────────────────────────────────────────────────────
  if (selectedCell.cellType === 'lineage') {
    const srcMat = materialNodes[selectedCell.sourceId];
    const tgtMat = materialNodes[selectedCell.targetId];
    const sourceLabel = srcMat
      ? `${srcMat.name} (${srcMat.container.name})`
      : selectedCell.sourceId;
    const targetLabel = tgtMat
      ? `${tgtMat.name} (${tgtMat.container.name})`
      : selectedCell.targetId;

    return (
      <InfoWrapper title="Lineage">
        <LineageInfo sourceLabel={sourceLabel} targetLabel={targetLabel} />
      </InfoWrapper>
    );
  }

  // ── Group ───────────────────────────────────────────────────────────────
  if (selectedCell.cellType === 'group') {
    const group = groups[selectedCell.id];
    if (!group) return <EmptyState />;

    const subSteps = group.stepIds
      .map((id) => (operationNodes[id] ? { id, ...operationNodes[id] } : null))
      .filter((s): s is NonNullable<typeof s> => s !== null);

    return (
      <InfoWrapper
        title={`Group: ${group.label}`}
        showEdit
        onEdit={() => onEditRequested(selectedCell.id)}
      >
        <GroupInfo data={{ id: selectedCell.id, ...group }} subSteps={subSteps} />
      </InfoWrapper>
    );
  }

  // ── Flow edge (basic edge info) ─────────────────────────────────────────
  if (selectedCell.cellType === 'edge') {
    const edge = flowEdges[selectedCell.id];
    if (!edge) return <EmptyState />;

    const srcMat = materialNodes[edge.sourceId];
    const srcOp = operationNodes[edge.sourceId];
    const tgtMat = materialNodes[edge.targetId];
    const tgtOp = operationNodes[edge.targetId];

    return (
      <InfoWrapper title="Edge">
        <EdgeInfo
          data={{ id: selectedCell.id, ...edge }}
          sourceLabel={srcMat?.name ?? srcOp?.displayName ?? edge.sourceId}
          sourceType={srcMat ? 'Material' : srcOp ? 'Operation' : 'Unknown'}
          targetLabel={tgtMat?.name ?? tgtOp?.displayName ?? edge.targetId}
          targetType={tgtMat ? 'Material' : tgtOp ? 'Operation' : 'Unknown'}
        />
      </InfoWrapper>
    );
  }

  return <EmptyState />;
};

// ─── Internal helper components ─────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
    <Typography variant="body2">Unknown cell selected.</Typography>
  </Box>
);

interface InfoWrapperProps {
  title: string;
  showEdit?: boolean;
  onEdit?: () => void;
  children: React.ReactNode;
}

const InfoWrapper: React.FC<InfoWrapperProps> = ({ title, showEdit, onEdit, children }) => (
  <Box sx={{ p: 1.5, overflowY: 'auto', height: '100%' }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        pb: 1,
        borderBottom: '2px solid #2563eb',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {showEdit && onEdit && (
        <IconButton size="small" onClick={onEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
);
